import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import useMiastoData from './hooks/useMiastoData'
import { applyGeoFilterAndNavigate } from '@/pages/klienci/common/klienciFiltersStorage'
import useMiastoModal from './hooks/useMiastoModal'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import MiastoTable from './components/MiastoTable'
import MiastoModal from './components/MiastoModal'
import PowiatFilterModal from '@/filter-modals/components/PowiatFilterModal'
import WojewodztwoFilterModal from '@/filter-modals/components/WojewodztwoFilterModal'
import GminaFilterModal from '@/filter-modals/components/GminaFilterModal'

export default function Miasto() {
	const navigate = useNavigate()
	const [isPowiatFilterOpen, setIsPowiatFilterOpen] = useState(false)
	const [isWojewodztwoFilterOpen, setIsWojewodztwoFilterOpen] = useState(false)
	const [isGminaFilterOpen, setIsGminaFilterOpen] = useState(false)

	const {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		setSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		searchQuery,
		filters,
		clearError,
	} = useMiastoData()

	const {
		isModalOpen,
		editingItem,
		formName,
		setFormName,
		formPowiatId,
		formPowiatName,
		formWojewodztwoId,
		formWojewodztwoName,
		formGminaId,
		formGminaName,
		isSaving,
		isPowiatSelectOpen,
		isWojewodztwoSelectOpen,
		isGminaSelectOpen,
		closeModal,
		handleSubmit,
		openPowiatSelectModal,
		closePowiatSelectModal,
		applyPowiatSelection,
		openWojewodztwoSelectModal,
		closeWojewodztwoSelectModal,
		applyWojewodztwoSelection,
		openGminaSelectModal,
		closeGminaSelectModal,
		applyGminaSelection,
		buttons,
	} = useMiastoModal(loadData)

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: 'Error',
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [error, clearError])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		updateFilters({ searchText: e.target.value })
	}

	const selectedPowiatId = filters.id_powiat || null
	const selectedWojewodztwoId = filters.id_wojewodztwo || null
	const selectedGminaId = filters.id_gmina || null

	const handleApplyPowiatFilter = powiatId => {
		const nextPowiatId = powiatId || null
		updateFilters({
			id_powiat: nextPowiatId,
			...(nextPowiatId !== selectedPowiatId && { id_gmina: null }),
		})
		setIsPowiatFilterOpen(false)
	}

	const handleApplyWojewodztwoFilter = wojewodztwoId => {
		const nextWojewodztwoId = wojewodztwoId || null
		updateFilters({
			id_wojewodztwo: nextWojewodztwoId,
			...(nextWojewodztwoId !== selectedWojewodztwoId && {
				id_powiat: null,
				id_gmina: null,
			}),
		})
		setIsWojewodztwoFilterOpen(false)
	}

	const handleApplyGminaFilter = gminaId => {
		updateFilters({ id_gmina: gminaId || null })
		setIsGminaFilterOpen(false)
	}

	const handleNazwaClick = miastoId =>
		applyGeoFilterAndNavigate(navigate, {
			targetPageKey: 'placowki',
			path: '/klienci/placowki',
			filterKey: 'id_miasto',
			filterValue: miastoId,
		})

	const hasPowiatFilter = !!selectedPowiatId
	const hasWojewodztwoFilter = !!selectedWojewodztwoId
	const hasGminaFilter = !!selectedGminaId

	return (
		<>
			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder="Szukaj miast po nazwie..."
				buttons={buttons}
			/>

			<MiastoTable
				data={data}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				hasPowiatFilter={hasPowiatFilter}
				hasWojewodztwoFilter={hasWojewodztwoFilter}
				hasGminaFilter={hasGminaFilter}
				onOpenPowiatFilter={() => setIsPowiatFilterOpen(true)}
				onOpenWojewodztwoFilter={() => setIsWojewodztwoFilterOpen(true)}
				onOpenGminaFilter={() => setIsGminaFilterOpen(true)}
				onGminaFilter={handleApplyGminaFilter}
				onPowiatFilter={handleApplyPowiatFilter}
				onWojewodztwoFilter={handleApplyWojewodztwoFilter}
				onNazwaClick={handleNazwaClick}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={data.length}
				totalCount={totalCount}
			/>
			<MiastoModal
				isOpen={isModalOpen}
				editingItem={editingItem}
				formName={formName}
				setFormName={setFormName}
				formPowiatId={formPowiatId}
				formPowiatName={formPowiatName}
				onOpenPowiatSelect={openPowiatSelectModal}
				formWojewodztwoId={formWojewodztwoId}
				formWojewodztwoName={formWojewodztwoName}
				onOpenWojewodztwoSelect={openWojewodztwoSelectModal}
				formGminaId={formGminaId}
				formGminaName={formGminaName}
				onOpenGminaSelect={openGminaSelectModal}
				isSaving={isSaving}
				onClose={closeModal}
				onSubmit={handleSubmit}
			/>
			<PowiatFilterModal
				isOpen={isPowiatFilterOpen}
				onClose={() => setIsPowiatFilterOpen(false)}
				selectedPowiatId={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyPowiatFilter}
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoFilterOpen}
				onClose={() => setIsWojewodztwoFilterOpen(false)}
				selectedWojewodztwoId={selectedWojewodztwoId}
				onApply={handleApplyWojewodztwoFilter}
			/>
			<GminaFilterModal
				isOpen={isGminaFilterOpen}
				onClose={() => setIsGminaFilterOpen(false)}
				selectedGminaId={selectedGminaId}
				idPowiat={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyGminaFilter}
			/>
			<PowiatFilterModal
				isOpen={isPowiatSelectOpen}
				onClose={closePowiatSelectModal}
				selectedPowiatId={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyPowiatSelection}
				title="Wybierz powiat"
				description="Wyszukaj i wybierz powiat dla miasta."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoSelectOpen}
				onClose={closeWojewodztwoSelectModal}
				selectedWojewodztwoId={formWojewodztwoId}
				onApply={applyWojewodztwoSelection}
				title="Wybierz wojewodztwo"
				description="Wyszukaj i wybierz wojewodztwo dla miasta."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<GminaFilterModal
				isOpen={isGminaSelectOpen}
				onClose={closeGminaSelectModal}
				selectedGminaId={formGminaId}
				idPowiat={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyGminaSelection}
				title="Wybierz gmine"
				description="Wyszukaj i wybierz gmine dla miasta."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
		</>
	)
}
